import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

import { posRuntimeDb } from "../runtime/local-store/pos-db";
import { readTerminalCredential } from "../runtime/local-store/terminal-state";

type CutoverDiagnosticState = {
  status: "CHECKING" | "READY" | "ERROR";
  error: string | null;
  scopeKey: string | null;
  journalTotal: number | null;
  unresolvedSaleOrders: number | null;
  outboxTotal: number | null;
  pending: number | null;
  sending: number | null;
  acked: number | null;
  failed: number | null;
};

const EMPTY_STATE: CutoverDiagnosticState = {
  status: "CHECKING",
  error: null,
  scopeKey: null,
  journalTotal: null,
  unresolvedSaleOrders: null,
  outboxTotal: null,
  pending: null,
  sending: null,
  acked: null,
  failed: null,
};

function terminalScopeKey(identity: {
  organization_id: string;
  branch_id: string;
  node_id: string;
}) {
  return `org:${identity.organization_id}:branch:${identity.branch_id}:node:${identity.node_id}`;
}

async function countOutboxState(scopeKey: string, state: "PENDING" | "SENDING" | "ACKED" | "FAILED") {
  return posRuntimeDb.outbox
    .where("scopeKey")
    .equals(scopeKey)
    .filter((record) => record.state === state)
    .count();
}

async function readDiagnosticState(): Promise<CutoverDiagnosticState> {
  await posRuntimeDb.open();
  const terminal = await readTerminalCredential();
  if (!terminal?.identity) {
    return {
      ...EMPTY_STATE,
      status: "ERROR",
      error: "TRUSTED_TERMINAL_IDENTITY_REQUIRED",
    };
  }

  const scopeKey = terminalScopeKey(terminal.identity);
  const scopedOutbox = posRuntimeDb.outbox.where("scopeKey").equals(scopeKey);
  const scopedJournal = posRuntimeDb.journal.where("scopeKey").equals(scopeKey);

  const [
    journalTotal,
    unresolvedSaleOrders,
    outboxTotal,
    pending,
    sending,
    acked,
    failed,
  ] = await Promise.all([
    scopedJournal.count(),
    scopedJournal
      .filter((record) => record.kind === "SALE_ORDER" && record.state !== "CONFIRMED")
      .count(),
    scopedOutbox.count(),
    countOutboxState(scopeKey, "PENDING"),
    countOutboxState(scopeKey, "SENDING"),
    countOutboxState(scopeKey, "ACKED"),
    countOutboxState(scopeKey, "FAILED"),
  ]);

  return {
    status: "READY",
    error: null,
    scopeKey,
    journalTotal,
    unresolvedSaleOrders,
    outboxTotal,
    pending,
    sending,
    acked,
    failed,
  };
}

export function OriginCutoverDiagnosticHost() {
  const [state, setState] = useState<CutoverDiagnosticState>(EMPTY_STATE);

  useEffect(() => {
    let active = true;

    const refresh = async () => {
      try {
        const next = await readDiagnosticState();
        if (active) setState(next);
      } catch (error) {
        if (!active) return;
        setState({
          ...EMPTY_STATE,
          status: "ERROR",
          error: error instanceof Error ? `${error.name}: ${error.message}` : String(error),
        });
      }
    };

    void refresh();
    const intervalId = window.setInterval(() => void refresh(), 2_000);
    return () => {
      active = false;
      window.clearInterval(intervalId);
    };
  }, []);

  const unresolved = state.pending == null || state.sending == null || state.failed == null
    ? null
    : state.pending + state.sending + state.failed;
  const cutoverGate = state.status !== "READY"
    ? "CHECKING"
    : unresolved === 0 && state.unresolvedSaleOrders === 0
      ? "READY"
      : "BLOCKED";
  const background = cutoverGate === "READY" ? "#eefbf0" : cutoverGate === "BLOCKED" ? "#fff1f0" : "#fff8dc";
  const border = cutoverGate === "READY" ? "#8fc79a" : cutoverGate === "BLOCKED" ? "#e49a94" : "#e9c75d";

  return createPortal(
    <section
      style={{
        position: "fixed",
        left: 24,
        right: 24,
        bottom: 24,
        zIndex: 100000,
        padding: "14px 18px",
        borderRadius: 14,
        border: `1px solid ${border}`,
        background,
        boxShadow: "0 14px 40px rgba(0,0,0,.18)",
        fontFamily: "system-ui, sans-serif",
        color: "#242424",
      }}
    >
      <strong style={{ display: "block", marginBottom: 6 }}>
        Origin Cutover Diagnostic — {cutoverGate}
      </strong>
      <div style={{ display: "flex", gap: 14, flexWrap: "wrap", fontSize: 12, lineHeight: 1.5 }}>
        <span>Origin: <b>{window.location.origin}</b></span>
        <span>Runtime: <b>{state.status}</b></span>
        <span>Journal: <b>{state.journalTotal ?? "?"}</b></span>
        <span>Unresolved Sale Orders: <b>{state.unresolvedSaleOrders ?? "?"}</b></span>
        <span>Outbox Total: <b>{state.outboxTotal ?? "?"}</b></span>
        <span>PENDING: <b>{state.pending ?? "?"}</b></span>
        <span>SENDING: <b>{state.sending ?? "?"}</b></span>
        <span>FAILED: <b>{state.failed ?? "?"}</b></span>
        <span>ACKED: <b>{state.acked ?? "?"}</b></span>
        <span>Unresolved Outbox: <b>{unresolved ?? "?"}</b></span>
      </div>
      {state.error ? (
        <code style={{ display: "block", marginTop: 7, whiteSpace: "normal", overflowWrap: "anywhere", fontSize: 11 }}>
          {state.error}
        </code>
      ) : null}
      <small style={{ display: "block", marginTop: 7 }}>
        อ่านอย่างเดียว · Scope ตาม Trusted Terminal ปัจจุบัน · ไม่แสดง payload/secret · ไม่แก้ Journal/Outbox · Outbox schema ปัจจุบันไม่มี CONFLICT state แยกต่างหาก
      </small>
    </section>,
    document.body,
  );
}
